import { useEffect, useState } from "react";
import { Star, Quote, Send } from "lucide-react";
import { api } from "../lib/api";

function StarRow({ value, onChange, size = 18, readOnly = false }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={readOnly}
          data-testid={readOnly ? `star-display-${s}` : `star-input-${s}`}
          onClick={() => !readOnly && onChange(s)}
          className={`${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110"} transition-transform`}
        >
          <Star
            size={size}
            className={s <= value ? "fill-orange-500 text-orange-500" : "text-stone-300"}
          />
        </button>
      ))}
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function ReviewsSection({ productId = null, compact = false }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/reviews${productId ? `?product_id=${productId}` : ""}`)
      .then((r) => setReviews(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, product_id: productId });
      setSuccess(true);
      setForm({ author: "", rating: 5, comment: "" });
      setShowForm(false);
      load();
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <section id="avis" data-testid="reviews-section" className="bg-stone-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="uppercase tracking-[0.18em] text-xs font-semibold text-stone-500">Témoignages</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-1">
              Ils nous ont fait confiance
            </h2>
            {avg && (
              <div className="mt-3 flex items-center gap-3">
                <StarRow value={Math.round(parseFloat(avg))} readOnly size={20} />
                <span data-testid="reviews-average" className="text-stone-700 font-medium">
                  {avg}/5
                </span>
                <span className="text-stone-500 text-sm">
                  · {reviews.length} avis
                </span>
              </div>
            )}
          </div>
          <button
            data-testid="open-review-form"
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-2.5 bg-orange-800 hover:bg-orange-900 text-white rounded-sm transition-colors"
          >
            {showForm ? "Annuler" : "Laisser un avis"}
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="mb-10 bg-white border border-stone-200 rounded-sm p-6 max-w-2xl"
          >
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Votre nom *</label>
                <input
                  data-testid="review-author"
                  required
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 bg-stone-50 border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 block mb-1">Note *</label>
                <StarRow value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size={24} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Votre avis *</label>
                <textarea
                  data-testid="review-comment"
                  required
                  rows={4}
                  maxLength={1000}
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 bg-stone-50 border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                />
              </div>
              {error && (
                <div data-testid="review-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{String(error)}</div>
              )}
              <button
                data-testid="review-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-sm disabled:bg-stone-400"
              >
                <Send size={16} /> {submitting ? "Envoi…" : "Publier mon avis"}
              </button>
            </div>
          </form>
        )}

        {success && (
          <div data-testid="review-success" className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-sm">
            Merci pour votre avis ! Il vient d'être publié.
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-sm h-48 animate-pulse" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div data-testid="reviews-empty" className="text-center py-12 bg-white border border-stone-200 rounded-sm">
            <p className="text-stone-500 font-serif text-xl">
              Soyez le premier à laisser un avis !
            </p>
          </div>
        ) : (
          <div data-testid="reviews-list" className={`grid grid-cols-1 ${compact ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"} gap-6`}>
            {reviews.slice(0, compact ? 4 : 9).map((r) => (
              <article
                key={r.id}
                data-testid={`review-${r.id}`}
                className="bg-white border border-stone-200 rounded-sm p-6 flex flex-col"
              >
                <Quote className="text-orange-700" size={22} />
                <p className="mt-3 text-stone-700 leading-relaxed flex-1">{r.comment}</p>
                <div className="mt-4 pt-4 border-t border-stone-100">
                  <StarRow value={r.rating} readOnly size={14} />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-medium text-stone-900">{r.author}</span>
                    <span className="text-xs text-stone-400">{formatDate(r.created_at)}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
